import * as React from "react";
import {cx} from "emotion";

import "./master-table.scss";

import {cc}                         from "@Common/react/callback-chain";
import {cs}                         from "@Common/react/chain-services";
import {keyed}                      from "@Common/react/keyed";
import {UseState}                   from "@Common/react/use-state";
import {compareSortValues, without} from "@Common/utils/collections";
import {chain}                      from "@Common/utils/fs";

export const MasterTable = (
    {
        list, columns, className=undefined, defaultSorting=undefined, defaultExpanded = undefined,
        sortState = undefined,
        noDataMessage="No data",
        noDataProps=undefined,
    }
) => cs(
    ["sorting", (_, next) => sortState ? next(sortState) : UseState({next})],

    keyed(list.length),
    ["expandedIndices", (_, next) => UseState({
        initValue: defaultExpanded == null ? (
            []
        ) : (
            list.map((item, i) => defaultExpanded(item, i) ? i : null).filter(v => v != null)
        ),
        next,
    })],
    ({sorting, expandedIndices}) => (
        <table className={cx("master-table master-table-00e", className, {"border-bottom": !expandedIndices.value?.includes(list.length-1)})}>
            <thead>
            <tr>
                <th className="padding left">&nbsp;</th>
                {columns.map((column, i) => (
                    <th {...{
                        className: cx(
                            "column",
                            {
                                sortable: !column.sorting?.disabled,
                                clickable: column.onClick,
                                sorted: sorting.value?.columnIndex === i,
                            },
                            column.align && "align-" + column.align,
                            column.className,
                        ),
                        key: i,
                        ...column.onClick ? {
                            onClick: () => column.onClick({
                                expanded: expandedIndices.value.length,
                                collapseAll: () => expandedIndices.change(() => []),
                                expandAll  : () => expandedIndices.change(() => list.map((_, i) => i)),
                            }),
                        } : !column.sorting?.disabled && {
                            onClick: () => {
                                if (expandedIndices.value.length) {
                                    expandedIndices.onChange([]);
                                }
                                sorting.change((s) => (s == null || s.columnIndex !== i) ? {
                                    columnIndex: i,
                                    direction: column.sorting?.direction ?? "asc",
                                } : s.direction === (column.sorting?.direction ?? "asc") ? {
                                    columnIndex: i,
                                    direction: (column.sorting?.direction ?? "asc") === "asc" ? "desc" : "asc",
                                } : null);
                            },
                        },
                        "xd-qa-type": column.xdQaType,
                        "xd-qa-name": column.xdQaName,
                    }}>
                        {column.render ? (
                            column.render({
                                expanded: expandedIndices.value.length,
                            })
                        ) : <>
                            <div className="label" xd-qa-type="label" xd-qa-name={column.label}>
                                {column.label}
                            </div>

                            {!column.sorting?.disabled && (
                                <i
                                    className={cx(
                                        "sort-indicator",
                                        sorting.value?.columnIndex === i && sorting.value.direction === "asc" ? (
                                            "fa fa-caret-up"
                                        ) : (
                                            "fa fa-caret-down"
                                        )
                                    )}
                                    xd-qa-type="icon"
                                    xd-qa-name={column.label + " sort"}
                                />
                            )}
                        </>}
                    </th>
                ))}
                <th className="padding right">&nbsp;</th>
            </tr>
            </thead>

            <tbody>
            {!list?.length && (
                <tr className="no-data">
                    <td className="padding">&nbsp;</td>
                    <td {...{
                        colSpan: columns.length,
                        className: "message",
                        ...noDataProps,
                    }}>
                        {noDataMessage}
                    </td>
                    <td className="padding">&nbsp;</td>
                </tr>
            )}
            {chain(
                list,
                (_) => (() => {
                    const list1 = _.slice(0);
                    list1.sort((row1,row2) => cc([
                        (_, next) => !defaultSorting ? next() : chain(
                            defaultSorting(row1.defaultSortingValue, row2.defaultSortingValue),
                            (sortResult) => sortResult || next(),
                        ),
                        (_, next) => !sorting.value ? 0 : next(),
                        (_, next) => (sorting.value.direction === "asc" ? 1 : -1) * next(),
                        () => {
                            const columnSorting = columns[sorting.value.columnIndex].sorting;
                            if (!columnSorting?.multi) {
                                const sv1 = row1.columns[sorting.value.columnIndex].sortValue();
                                const sv2 = row2.columns[sorting.value.columnIndex].sortValue();

                                return sv1 < sv2 ? -1 : sv1 > sv2 ? 1 : 0;
                            } else {

                                const sv1 = row1.columns[sorting.value.columnIndex].sortValues()
                                const sv2 = row2.columns[sorting.value.columnIndex].sortValues()

                                return compareSortValues(sv1, sv2);
                            }
                        },
                    ]));
                    return list1;
                })(),
                (_) => _.map((row, i) => cs(
                    keyed(row.key ?? i),
                    ({}) => {
                        const expanded = expandedIndices.value.includes(i);

                        const renderParams = {
                            expanded,
                            setExpanded: (expanded) => (
                                expandedIndices.change((indices) => !expanded ? (
                                    without(i, indices)
                                ) : (
                                    [...indices, i]
                                ))
                            ),
                            index: i,
                        };

                        return <>
                            <tr
                                className={cx(
                                    "master-record",
                                    {
                                        expanded,
                                        grey: !expanded && i % 2 === 1,
                                    },
                                )}
                                onClick={row.onClick && (() => row.onClick(renderParams))}
                                xd-qa-type={row.xdQaType}
                                xd-qa-name={row.xdQaName}
                            >
                                <td className="padding">&nbsp;</td>

                                {columns.map((column, j) => {
                                    const rowColumn = row.columns[j];
                                    return (
                                        <td
                                            className={cx(
                                                "column", column.align && "align-" + column.align,
                                                rowColumn.className,
                                            )}
                                            xd-qa-type={(rowColumn.xdQaType ?? "label") || undefined}
                                            xd-qa-name={(rowColumn.xdQaName ?? column.label + " " + (row.rowId ?? i)) || undefined}
                                            key={j}
                                            onClick={rowColumn.onClick && (() => rowColumn.onClick(renderParams))}
                                        >
                                            {rowColumn.renderCell(renderParams)}
                                        </td>
                                    );
                                })}

                                <td className="padding">&nbsp;</td>
                            </tr>

                            {expanded && (
                                <tr className="expansion" xd-qa-type={row.xdQaType} xd-qa-name={row.xdQaName + " expanded"}>
                                    <td colSpan={columns.length + 2}> {/* 2 for 2 padding cells*/}
                                        {row.renderExpansion(i)}
                                    </td>
                                </tr>
                            )}
                        </>;
                    },
                )),
            )}
            </tbody>
        </table>
    ),
);
